import api from "@/utils/api";
import type { ApiResponse } from "@/utils/api/ApiProps";

// Type definitions for session and login history
interface LoginHistoryRecord {
  history_uuid: string;
  user_uuid: string;
  user_name?: string;
  user_email?: string;
  ip_address: string;
  device_info: string;
  user_agent: string;
  login_method: 'email' | 'facebook' | 'google' | 'whatsapp';
  login_status: 'success' | 'failed';
  failure_reason?: string;
  login_at: string;
  logout_at?: string;
  logout_type?: 'manual' | 'auto' | 'forced';
  session_duration?: number;
}

interface ActiveSession {
  session_uuid: string;
  user_uuid: string;
  user_name: string;
  user_email: string;
  ip_address: string;
  device_info: string;
  user_agent?: string;
  login_method: 'email' | 'facebook' | 'google' | 'whatsapp';
  login_at: string;
  last_activity_at: string;
  is_current?: boolean;
}

/**
 * Get current user's login history
 */
export const getMyLoginHistory = async (limit: number = 50): Promise<ApiResponse<{ history: LoginHistoryRecord[] }>> => {
  const response = await api.req({
    url: `/sessions/my-history?limit=${limit}`,
    method: api.Method.GET,
  });
  return response as ApiResponse<{ history: LoginHistoryRecord[] }>;
};

/**
 * Get current user's active sessions
 */
export const getMySessions = async (): Promise<ApiResponse<{ sessions: ActiveSession[] }>> => {
  const response = await api.req({
    url: `/sessions/my-sessions`,
    method: api.Method.GET,
  });
  return response as ApiResponse<{ sessions: ActiveSession[] }>;
};

/**
 * Logout current user's own session (not current one)
 */
export const logoutMySession = async (sessionUuid: string): Promise<ApiResponse<{ message: string }>> => {
  const response = await api.req({
    url: `/sessions/${sessionUuid}`,
    method: api.Method.DELETE,
  });
  return response as ApiResponse<{ message: string }>;
};

/**
 * Admin: Get all active sessions
 */
export const getAllActiveSessions = async (): Promise<ApiResponse<{ sessions: ActiveSession[] }>> => {
  const response = await api.req({
    url: `/admin/sessions/active`,
    method: api.Method.GET,
  });
  return response as ApiResponse<{ sessions: ActiveSession[] }>;
};

/**
 * Admin: Get specific user's sessions
 */
export const getUserSessions = async (userUuid: string): Promise<ApiResponse<{ sessions: ActiveSession[] }>> => {
  const response = await api.req({
    url: `/admin/sessions/user/${userUuid}`,
    method: api.Method.GET,
  });
  return response as ApiResponse<{ sessions: ActiveSession[] }>;
};

/**
 * Admin: Force logout session
 */
export const forceLogoutSession = async (sessionUuid: string): Promise<ApiResponse<{ message: string }>> => {
  const response = await api.req({
    url: `/admin/sessions/${sessionUuid}`,
    method: api.Method.DELETE,
  });
  return response as ApiResponse<{ message: string }>;
};

/**
 * Admin: Get all login history with filters
 */
export const getAllLoginHistory = async (params?: {
  user_uuid?: string;
  login_status?: string;
  login_method?: string;
  ip_address?: string;
  limit?: number;
  offset?: number;
}): Promise<ApiResponse<{ history: LoginHistoryRecord[] }>> => {
  const queryParams = new URLSearchParams();
  if (params?.user_uuid) queryParams.append('user_uuid', params.user_uuid);
  if (params?.login_status) queryParams.append('login_status', params.login_status);
  if (params?.login_method) queryParams.append('login_method', params.login_method);
  if (params?.ip_address) queryParams.append('ip_address', params.ip_address);
  if (params?.limit) queryParams.append('limit', params.limit.toString());
  if (params?.offset) queryParams.append('offset', params.offset.toString());

  const response = await api.req({
    url: `/admin/login-history?${queryParams.toString()}`,
    method: api.Method.GET,
  });
  return response as ApiResponse<{ history: LoginHistoryRecord[] }>;
};

/**
 * Admin: Get specific user's login history
 */
export const getUserLoginHistory = async (userUuid: string, limit: number = 100): Promise<ApiResponse<{ history: LoginHistoryRecord[] }>> => {
  const response = await api.req({
    url: `/admin/login-history/user/${userUuid}?limit=${limit}`,
    method: api.Method.GET,
  });
  return response as ApiResponse<{ history: LoginHistoryRecord[] }>;
};
